import React, { useState } from 'react';
import api from '../lib/api';
import { 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  User, 
  Building2,
  Briefcase,
  Users,
  UserX,
  FileWarning,
  Loader2,
  Info,
  Phone,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const ConflictSearch = () => {
  const [searchParams, setSearchParams] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate at least one field
    if (!searchParams.name && !searchParams.company && !searchParams.email && !searchParams.phone) {
      setError('Please enter at least one search parameter');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const params = new URLSearchParams();
      if (searchParams.name) params.append('name', searchParams.name);
      if (searchParams.company) params.append('company', searchParams.company);
      if (searchParams.email) params.append('email', searchParams.email);
      if (searchParams.phone) params.append('phone', searchParams.phone);

      const response = await api.get(`/conflicts/search?${params.toString()}`);
      setResults(response.data);
      
      // Add to search history
      setSearchHistory(prev => [
        { ...searchParams, timestamp: new Date().toISOString(), hasConflict: response.data.conflicts_found },
        ...prev.slice(0, 9)
      ]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to perform conflict search');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchParams({ name: '', company: '', email: '', phone: '' });
    setResults(null);
    setError(null);
  };

  const handleQuickSearch = (historyItem) => {
    setSearchParams({
      name: historyItem.name || '',
      company: historyItem.company || '',
      email: historyItem.email || '',
      phone: historyItem.phone || ''
    });
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'client':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'lead':
        return <User className="h-4 w-4 text-green-500" />;
      case 'opposing_party':
        return <UserX className="h-4 w-4 text-red-500" />;
      case 'related_party':
        return <Building2 className="h-4 w-4 text-orange-500" />;
      default:
        return <FileWarning className="h-4 w-4 text-slate-500" />;
    }
  };

  const getResultBadge = (type) => {
    switch (type) {
      case 'client':
        return <Badge className="bg-blue-100 text-blue-700">Client</Badge>;
      case 'lead':
        return <Badge className="bg-green-100 text-green-700">Lead</Badge>;
      case 'opposing_party':
        return <Badge className="bg-red-100 text-red-700">Opposing Party</Badge>;
      case 'related_party':
        return <Badge className="bg-orange-100 text-orange-700">Related Party</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="conflict-search-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Conflict of Interest Search</h1>
        <p className="text-slate-500 mt-1">
          Search across clients, leads, cases, and opposing parties to identify potential conflicts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Parameters
              </CardTitle>
              <CardDescription>
                Enter one or more search criteria to find potential conflicts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="Person or company name"
                      value={searchParams.name}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="conflict-search-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Company/Organization
                    </Label>
                    <Input
                      id="company"
                      placeholder="Company or organization"
                      value={searchParams.company}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, company: e.target.value }))}
                      data-testid="conflict-search-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Email address"
                      value={searchParams.email}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="conflict-search-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      placeholder="Phone number"
                      value={searchParams.phone}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, phone: e.target.value }))}
                      data-testid="conflict-search-phone"
                    />
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={loading} data-testid="conflict-search-submit">
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Search for Conflicts
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleClear}>
                    Clear
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {results && (
            <Card data-testid="conflict-search-results">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {results.conflicts_found ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        Potential Conflicts Found
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        No Conflicts Found
                      </>
                    )}
                  </CardTitle>
                  <Badge variant={results.conflicts_found ? "destructive" : "secondary"}>
                    {results.total_matches} match{results.total_matches !== 1 ? 'es' : ''}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {results.conflicts_found ? (
                  <Tabs defaultValue="all" className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="all">
                        All ({results.total_matches})
                      </TabsTrigger>
                      <TabsTrigger value="clients">
                        Clients ({results.clients?.length || 0})
                      </TabsTrigger>
                      <TabsTrigger value="leads">
                        Leads ({results.leads?.length || 0})
                      </TabsTrigger>
                      <TabsTrigger value="opposing">
                        Opposing ({results.opposing_parties?.length || 0})
                      </TabsTrigger>
                      <TabsTrigger value="related">
                        Related ({results.related_parties?.length || 0})
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all" className="mt-4">
                      <div className="space-y-4">
                        {/* Clients */}
                        {results.clients?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <Users className="h-4 w-4" /> Existing Clients
                            </h4>
                            {results.clients.map((client, idx) => (
                              <div key={idx} className="p-3 bg-blue-50 rounded-lg mb-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{client.name}</p>
                                    <p className="text-sm text-slate-500">{client.email}</p>
                                  </div>
                                  {getResultBadge('client')}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Leads */}
                        {results.leads?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <User className="h-4 w-4" /> Leads
                            </h4>
                            {results.leads.map((lead, idx) => (
                              <div key={idx} className="p-3 bg-green-50 rounded-lg mb-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{lead.name}</p>
                                    <p className="text-sm text-slate-500">{lead.email}</p>
                                  </div>
                                  {getResultBadge('lead')}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Opposing Parties */}
                        {results.opposing_parties?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <UserX className="h-4 w-4" /> Opposing Parties
                            </h4>
                            {results.opposing_parties.map((party, idx) => (
                              <div key={idx} className="p-3 bg-red-50 rounded-lg mb-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{party.name}</p>
                                    <p className="text-sm text-slate-500">
                                      Case: {party.case_title}
                                      {party.counsel && ` • Legal Rep: ${party.counsel}`}
                                    </p>
                                  </div>
                                  {getResultBadge('opposing_party')}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Related Parties */}
                        {results.related_parties?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <Building2 className="h-4 w-4" /> Related Parties
                            </h4>
                            {results.related_parties.map((party, idx) => (
                              <div key={idx} className="p-3 bg-orange-50 rounded-lg mb-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{party.name}</p>
                                    <p className="text-sm text-slate-500">
                                      Case: {party.case_title}
                                      {party.role && ` • Role: ${party.role}`}
                                    </p>
                                  </div>
                                  {getResultBadge('related_party')}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="clients" className="mt-4">
                      {results.clients?.length > 0 ? (
                        <div className="space-y-2">
                          {results.clients.map((client, idx) => (
                            <div key={idx} className="p-4 border rounded-lg">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-lg">{client.name}</p>
                                  <div className="text-sm text-slate-500 mt-1 space-y-1">
                                    {client.email && <p>Email: {client.email}</p>}
                                    {client.phone && <p>Phone: {client.phone}</p>}
                                    {client.company && <p>Company: {client.company}</p>}
                                  </div>
                                </div>
                                <Button variant="outline" size="sm" asChild>
                                  <a href={`/clients/${client.client_id}`}>View Client</a>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-slate-500 py-8">No matching clients found</p>
                      )}
                    </TabsContent>

                    <TabsContent value="leads" className="mt-4">
                      {results.leads?.length > 0 ? (
                        <div className="space-y-2">
                          {results.leads.map((lead, idx) => (
                            <div key={idx} className="p-4 border rounded-lg">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-lg">{lead.name}</p>
                                  <div className="text-sm text-slate-500 mt-1 space-y-1">
                                    {lead.email && <p>Email: {lead.email}</p>}
                                    {lead.phone && <p>Phone: {lead.phone}</p>}
                                    {lead.source && <p>Source: {lead.source}</p>}
                                  </div>
                                </div>
                                <Button variant="outline" size="sm" asChild>
                                  <a href={`/leads`}>View Leads</a>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-slate-500 py-8">No matching leads found</p>
                      )}
                    </TabsContent>

                    <TabsContent value="opposing" className="mt-4">
                      {results.opposing_parties?.length > 0 ? (
                        <div className="space-y-2">
                          {results.opposing_parties.map((party, idx) => (
                            <div key={idx} className="p-4 border rounded-lg border-red-200 bg-red-50">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-lg">{party.name}</p>
                                  <div className="text-sm text-slate-600 mt-1 space-y-1">
                                    <p>Case: {party.case_title}</p>
                                    {party.counsel && <p>Opposing Legal Representative: {party.counsel}</p>}
                                  </div>
                                </div>
                                <Button variant="outline" size="sm" asChild>
                                  <a href={`/cases/${party.case_id}`}>View Case</a>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-slate-500 py-8">No matching opposing parties found</p>
                      )}
                    </TabsContent>

                    <TabsContent value="related" className="mt-4">
                      {results.related_parties?.length > 0 ? (
                        <div className="space-y-2">
                          {results.related_parties.map((party, idx) => (
                            <div key={idx} className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-lg">{party.name}</p>
                                  <div className="text-sm text-slate-600 mt-1 space-y-1">
                                    <p>Case: {party.case_title}</p>
                                    {party.role && <p>Role: {party.role}</p>}
                                  </div>
                                </div>
                                <Button variant="outline" size="sm" asChild>
                                  <a href={`/cases/${party.case_id}`}>View Case</a>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-slate-500 py-8">No matching related parties found</p>
                      )}
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">No Conflicts Detected</h3>
                    <p className="text-slate-500 mt-2">
                      The search did not find any matching records in your database.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                About Conflict Checks
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-3">
              <p>
                Conflict of interest checks are essential for maintaining ethical standards
                in legal practice.
              </p>
              <p>
                This search will look through:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-500">
                <li>Current and past clients</li>
                <li>Leads in your pipeline</li>
                <li>Opposing parties from cases</li>
                <li>Related parties and witnesses</li>
              </ul>
              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription className="text-xs">
                  This tool assists with conflict checking but should not replace
                  thorough due diligence and professional judgment.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Search History */}
          {searchHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Searches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {searchHistory.slice(0, 5).map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickSearch(item)}
                      className="w-full text-left p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm truncate">
                          {item.name || item.company || item.email || item.phone}
                        </div>
                        {item.hasConflict ? (
                          <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConflictSearch;
